import { Alert, Col, Form, Row, Typography } from "antd";
import { Spin } from "antd-v5";
import { BaseModalRef } from "components/BaseModal";
import { DEFAULTS, requiredValidation } from "components/CustomDuration";
import message from "components/CustomMessage";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { FindingBucketContextType } from "contexts/FindingBucketContext";
import useEnabledFindingStatus from "hooks/useEnabledFindingStatus";
import useProposeFlowCustomFields, {
  getFieldValueFromFormValue,
} from "hooks/useProposeFlowCustomFields";
import _ from "lodash";
import moment from "moment";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { CustomFieldFindingPayload } from "types/api/customField";
import { PROPOSE_TYPE } from "types/tenantConfig";
import { hasPermission } from "utils/authService";
import { FieldTypes } from "utils/formUtils";
import { getTicketSystemName } from "views/Integrations/new/util";

import {
  BULK_FINDING_ACTION_MAP,
  DEVELOPER_EMAIL_SUFFIX,
  FINDING_STATUS,
  FINDING_STATUS_DISPLAY_TEXT,
  FINDING_STATUS_VOCABULARY_MAP,
} from "../Constants";
import { disabledActionsForSelectAll } from "../FindingsListV3";
import { getCustomFieldProps, isCustomFinding } from "../utils";
import AcceptRiskThresholdAlert from "./AcceptRiskThresholdAlert";
import BulkUpdatePopup from "./BulkUpdatePopup";
import TicketCreatedWarning from "./TicketCreatedWarning";
import TicketInProgressWarning from "./TicketInProgressWarning";

type Props = {
  afterClose: () => void;
  isPropose: boolean;
  bucket: FindingBucketContextType;
  history: any; // @TODO - history type fix
};

const BulkChangeStatusPopup = forwardRef<BaseModalRef, Props>(
  ({ afterClose, isPropose, bucket, history }, ref) => {
    const [form] = Form.useForm();

    const [findingName, statusName, placeholderName] =
      bucket.totalCount > 1
        ? ["findings", "statuses", "multiple values"]
        : ["finding", "status", "select value"];
    const [selectedStatus, setSelectedStatus] = useState<string>(); // @TODO - better typing when available
    const [multipleProductSubproduct, setMultipleProductSubproduct] =
      useState(false);
    const [createTicket, setCreateTicket] = useState(false);
    const [systemError, setSystemError] = useState(false);

    const [system, setSystem] = useState<any>();
    const [isTicketInProgress, setIsTicketInProgress] = useState(false);
    const [isAcceptRiskThresholdReached, setIsAcceptRiskThresholdReached] =
      useState(false);
    const enabledFindingStatus = useEnabledFindingStatus();

    const isControlled = selectedStatus === "CONTROLLED";
    const isAcceptRisk = selectedStatus === "ACCEPTRISK";
    const isConfirm = selectedStatus === "CONFIRMED";
    const isAcceptRiskOrControlled = isControlled || isAcceptRisk;
    const { findings: findingsArray, filterMap: selectedFilters } = bucket;

    const { findingsWithTicketCount, findingsWithTicketWarning } =
      TicketCreatedWarning(findingsArray);
    const allTicketsCreated = findingsWithTicketCount === findingsArray?.length;

    const areAllFildingsSelected = !_.isEmpty(selectedFilters);
    const isMitigatedDisabled = useMemo(() => {
      let isDisabled = !hasPermission(PermissionSetMap.MARK_MITIGATED);
      if (!isDisabled) {
        isDisabled = !findingsArray.every((finding) =>
          isCustomFinding(finding.source),
        );
      }
      if (
        areAllFildingsSelected &&
        disabledActionsForSelectAll.includes("MITIGATED")
      ) {
        isDisabled = true;
      }
      return isDisabled;
    }, [areAllFildingsSelected, findingsArray]);

    const isProposeMitigatedDisabled = useMemo(() => {
      let isDisabled = !hasPermission(PermissionSetMap.PROPOSE);
      if (!isDisabled) {
        isDisabled = !findingsArray.every((finding) =>
          isCustomFinding(finding.source),
        );
      }
      if (
        areAllFildingsSelected &&
        disabledActionsForSelectAll.includes("PROPOSE_MITIGATED")
      ) {
        isDisabled = true;
      }
      return isDisabled;
    }, [areAllFildingsSelected, findingsArray]);

    const [loading, initialConfig, availableCustomFields] =
      useProposeFlowCustomFields(!!isPropose);

    const mandatoryCustomFields = useMemo(() => {
      let type: string;
      switch (selectedStatus) {
        case "FALSEPOSITIVE":
          type = "FALSE_POSITIVE";
          break;
        default:
          type = selectedStatus ?? "";
          break;
      }
      const mandatoryFieldIds = initialConfig[`PROPOSE_${type}` as PROPOSE_TYPE]
        ?.checked
        ? (initialConfig[`PROPOSE_${type}` as PROPOSE_TYPE]?.customFields ?? [])
        : [];
      return availableCustomFields.filter((field) =>
        mandatoryFieldIds.includes(field.id),
      );
    }, [initialConfig, availableCustomFields, selectedStatus]);

    useEffect(() => {
      const statuses: string[] = []; // @TODO: use status type
      if (_.isEmpty(selectedFilters)) {
        findingsArray.forEach((finding) => {
          if (finding.status && statuses.indexOf(finding.status) === -1) {
            statuses.push(finding.status);
          }
        });
      }
      if (
        statuses.length === 1 &&
        !(isPropose && (FINDING_STATUS.OPEN || FINDING_STATUS.CONFIRMED))
      ) {
        form.setFieldsValue({
          status: statuses[0],
          unsupressAfterDuration: DEFAULTS,
        });
        setSelectedStatus(statuses[0]);
      } else {
        form.setFieldsValue({
          unsupressAfterDuration: DEFAULTS,
        });
      }
    }, [findingsArray, form, selectedFilters, isPropose]);

    useEffect(() => {
      const productsArray: number[] = [];
      const subProductArray: number[] = [];
      const assignToArmorCodeUserMap: any = {};

      findingsArray.forEach((finding) => {
        if (
          finding.product?.id &&
          productsArray.indexOf(finding.product.id) === -1
        ) {
          productsArray.push(finding.product.id);
        }
        if (
          finding.subProduct?.id &&
          subProductArray.indexOf(finding.subProduct.id) === -1
        ) {
          subProductArray.push(finding.subProduct.id);
        }
        if (finding.developer) {
          assignToArmorCodeUserMap[finding.developer] = finding.developer;
        }
      });

      if (productsArray.length > 1 || subProductArray.length > 1) {
        setMultipleProductSubproduct(true);
      } else {
        setMultipleProductSubproduct(false);
      }

      const developerKeys = Object.keys(assignToArmorCodeUserMap);
      if (developerKeys.length === 1) {
        const [developerName] = developerKeys;
        if (_.includes(developerName, DEVELOPER_EMAIL_SUFFIX)) {
          // Only for single developer with armorcode.io email
          form.setFieldsValue({ developer: developerName });
        }
      }

      if (productsArray.length === 1) {
        ToolsIntegrationsAPI.getJiraIntegrationByProductSubProduct({
          product: productsArray[0],
          subProduct: subProductArray[0],
        }).promise.then((configuration) => {
          if (configuration) {
            setSystem(configuration);
            if (configuration.operationalStatus === "CONFIG_ERROR") {
              setSystemError(true);
            }
          }
        });
      }
    }, [findingsArray, form]);

    const onTicketCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCreateTicket(e.target.checked);
    };

    const onOk = async (data: any) => {
      const { status } = data;

      const unSuppress = data.unsupressAfterDuration?.neverOrAfter !== "Never";
      data.unsupressAfterDuration =
        unSuppress && data.unsupressAfterDuration
          ? moment()
              .add(
                +data.unsupressAfterDuration.number,
                _.lowerCase(
                  data.unsupressAfterDuration.durationType,
                ) as moment.unitOfTime.DurationConstructor,
              )
              .valueOf()
          : undefined;

      data = {
        ...data,
        unSupress: isAcceptRiskOrControlled ? unSuppress : undefined,
      };

      if (isPropose) {
        delete data.status;

        const customFields: CustomFieldFindingPayload[] = [];
        if (!_.isEmpty(mandatoryCustomFields)) {
          _.forEach(mandatoryCustomFields, (field) => {
            const value = data[field.id];
            if (!_.isNil(value)) {
              const fieldValue = getFieldValueFromFormValue(value, field);
              customFields.push({
                customFieldId: field.id,
                customFieldValue: fieldValue,
                customFieldLabel: field.label,
                customFieldType: field.type || "text",
              });
            }
          });

          data["findingCustomFieldsUpdateDtos"] = _.isEmpty(customFields)
            ? undefined
            : customFields;
        }
      }

      let apiType;
      switch (status) {
        case "OPEN":
          apiType = isPropose ? undefined : "reopen";
          break;
        case "CONFIRMED":
          apiType = isPropose ? undefined : "confirmed";
          break;
        case "TRIAGE":
          apiType = isPropose ? "proposeTriage" : "triage";
          break;
        case "IN_PROGRESS":
          apiType = isPropose ? "proposeInProgress" : "inProgress";
          break;
        case "ACCEPTRISK":
          apiType = isPropose ? "proposeAcceptRisk" : "acceptRisk";
          break;
        case "FALSEPOSITIVE":
          apiType = isPropose ? "proposeFalsePositive" : "falsePositive";
          break;
        case "SUPPRESSED":
          apiType = isPropose ? "proposeSuppressed" : "suppressed";
          break;
        case "CONTROLLED":
          apiType = isPropose ? "proposeControlled" : "controlled";
          break;
        case "MITIGATED":
          apiType = isPropose ? "proposeMitigated" : "mitigated";
          break;
        default:
          break;
      }

      if (apiType) {
        // @ts-expect-error
        await bucket.submit(data, FindingAPI.bulk[apiType]);
      } else {
        message.error("Wrong status value selected");
      }

      message.success(
        <Row>
          {!isPropose && "Marked as"}{" "}
          {isPropose ? (
            <>{FINDING_STATUS_DISPLAY_TEXT[status]} has been Proposed</>
          ) : (
            FINDING_STATUS_DISPLAY_TEXT[data.status]
          )}{" "}
          for {bucket.totalCount} finding(s). Please check after some time.
        </Row>,
      );
    };

    const status = useMemo(() => {
      const allStatuses = _.map(enabledFindingStatus, (status) => {
        let statusType = isPropose ? `PROPOSE_${status}` : status;

        if (isPropose && status === "FALSEPOSITIVE") {
          statusType = "PROPOSE_FALSE_POSITIVE";
        }

        let shouldDisable = !hasPermission(
          // @ts-expect-error - Element implicitly has an 'any' type
          PermissionSetMap[BULK_FINDING_ACTION_MAP[statusType]],
        );

        if (status === "MITIGATED") {
          shouldDisable = isPropose
            ? isProposeMitigatedDisabled
            : isMitigatedDisabled;
        }

        return {
          label: FINDING_STATUS_DISPLAY_TEXT[status],
          value: status,
          filterLabel: getVocabulary(FINDING_STATUS_VOCABULARY_MAP[status]),
          disabled: shouldDisable,
          title: shouldDisable ? "No Access" : undefined,
        };
      });

      // Do not show Open and confirm for propose flow
      if (isPropose) {
        return allStatuses.filter(
          (entry) =>
            [FINDING_STATUS.OPEN, FINDING_STATUS.CONFIRMED].indexOf(
              entry.value as string,
            ) === -1,
        );
      }
      return allStatuses;
    }, [
      isMitigatedDisabled,
      isPropose,
      isProposeMitigatedDisabled,
      enabledFindingStatus,
    ]);

    const fields = [
      {
        name: "status",
        label: "New status",
        placeholder: placeholderName,
        required: true,
        type: FieldTypes.SELECT,
        options: status,
        props: {
          optionFilterProp: "filterLabel",
          onChange: (status: string) => {
            const { unsupressAfterDuration = {} } = form.getFieldsValue();
            form.setFieldsValue({
              createTicket: false,
              unsupressAfterDuration:
                status === "ACCEPTRISK" || status === "CONTROLLED"
                  ? {
                      ...unsupressAfterDuration,
                      neverOrAfter: "After",
                    }
                  : undefined,
            });
            setCreateTicket(false);
            setSelectedStatus(status);
          },
        },
      },
      ...(!isPropose && isAcceptRiskOrControlled
        ? [
            {
              name: "reason",
              label: "Reason",
              type: FieldTypes.SELECT,
              required: true,
              colSpan: 12,
              props: {
                api: isAcceptRisk
                  ? FindingAPI.getAcceptRiskReasons
                  : FindingAPI.getControlledReasons,
              },
            },
            {
              name: "unsupressAfterDuration",
              label: (
                <>
                  Re-
                  <Vocabulary name="open" />
                </>
              ),
              colSpan: 12,
              type: FieldTypes.CUSTOM_DURATION,
              required: true,
              rules: [requiredValidation],
              props: {
                showNeverOption: isControlled,
              },
            },
          ]
        : []),
      ...(!isPropose && (isAcceptRiskOrControlled || isConfirm)
        ? [
            {
              name: "createTicket",
              label: system ? (
                `Create ${getTicketSystemName(system.ticketSystemType)} Ticket`
              ) : (
                <span className="d-inline">
                  Ticketing System not configured. Click{" "}
                  <Typography.Link
                    onClick={() => history.push("/integrations")}
                  >
                    here
                  </Typography.Link>{" "}
                  to configure.
                </span>
              ),
              colSpan: 12,
              type: system ? FieldTypes.BOOLEAN : FieldTypes.LABEL,
              permission: PermissionSetMap.CREATE_TICKETS,
              disabled: systemError || !_.isEmpty(selectedFilters),
              toolTipPlacement: "right",
              help: systemError
                ? "Ticket system configuration has errored, ticket creation may fail. Please check configuration."
                : undefined,
              props: {
                onChange: onTicketCheckChange,
                value: createTicket,
              },
            },
          ]
        : []),
      ...(createTicket
        ? [
            {
              name: "developer",
              label: "Assign To",
              type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
              colSpan: 12,
              options: [{ label: "Unassigned", value: null }],
              props: system?.id
                ? {
                    api: ToolsIntegrationsAPI.getTicketingUsers,
                    apiParams: { configId: system?.id },
                  }
                : {},
            },
          ]
        : []),
      ...(!_.isEmpty(mandatoryCustomFields)
        ? [
            ..._.map(mandatoryCustomFields, (field) => {
              const customProps = getCustomFieldProps(field, true);

              return {
                name: field.id,
                label: field.label,
                required: true,
                ...customProps,
              };
            }),
          ]
        : []),
    ];

    const shouldDisableAcceptRiskOrControlled = createTicket
      ? multipleProductSubproduct ||
        isTicketInProgress ||
        (isAcceptRisk && isAcceptRiskThresholdReached)
      : isAcceptRisk && isAcceptRiskThresholdReached;
    const modalProps = {
      okText: `${isPropose ? "Propose" : "Change"} ${bucket.totalCount} ${statusName}`,
      disabled: isAcceptRiskOrControlled && shouldDisableAcceptRiskOrControlled,
    };

    return (
      <BulkUpdatePopup
        ref={ref}
        title={
          <>
            {isPropose ? "Propose " : "Bulk "}status change
            <div className="pull-right text-bold info">
              {bucket.totalCount} {findingName}
            </div>
          </>
        }
        form={form}
        onOk={onOk}
        bucket={bucket}
        fields={fields}
        modalProps={modalProps}
        afterClose={afterClose}
      >
        <Spin spinning={loading}>
          {isAcceptRiskOrControlled && (
            <>
              {isAcceptRisk && (
                <Row>
                  <Col span={24}>
                    <AcceptRiskThresholdAlert
                      findingIds={bucket.findingIds}
                      setIsAcceptRiskThresholdReached={
                        setIsAcceptRiskThresholdReached
                      }
                    />
                  </Col>
                </Row>
              )}
              {createTicket &&
                !multipleProductSubproduct &&
                findingsArray?.length > 0 && (
                  <TicketInProgressWarning
                    setIsTicketInProgress={setIsTicketInProgress}
                    findings={findingsArray}
                  />
                )}
              {createTicket &&
                !multipleProductSubproduct &&
                findingsArray?.length > 0 &&
                findingsWithTicketWarning}
              {createTicket && multipleProductSubproduct && (
                <Row>
                  <Col span={24}>
                    <Alert
                      type="error"
                      showIcon
                      message={
                        <>
                          To create ticket, please select findings from single{" "}
                          <Vocabulary name={"product"} /> and{" "}
                          <Vocabulary name={"subProduct"} />.
                        </>
                      }
                    />
                  </Col>
                </Row>
              )}
              {createTicket && systemError && !allTicketsCreated && (
                <Alert
                  className="m-b-xs"
                  type="error"
                  showIcon
                  message={
                    "Ticket system configuration has errored, ticket creation may fail. Please check configuration."
                  }
                />
              )}
            </>
          )}
        </Spin>
      </BulkUpdatePopup>
    );
  },
);

export default BulkChangeStatusPopup;
